import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";

export default function ProductsPage() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const line = params.get("linea") || params.get("line") || "";
  const species = params.get("specie") || params.get("species") || "";

  useEffect(() => {
    const req = { params: {} };
    if (line) req.params.line = line;
    if (species) req.params.species = species;
    if (q) req.params.q = q;
    api.get("/products", req).then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, [line, species, q]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  const petCount = useMemo(() => products.filter((p) => p.line === "pet").length, [products]);
  const humanCount = useMemo(() => products.filter((p) => p.line === "human").length, [products]);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20" data-testid="products-page">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">
          {t.__lang === "en" ? "Catalog" : "Catalogo"}
        </div>
        <h1 className="font-display text-5xl md:text-6xl text-brand leading-tight">
          {t.__lang === "en" ? "BioNutrix Products" : "Prodotti BioNutrix"}
        </h1>
        <p className="mt-4 text-muted-ink max-w-2xl">
          {t.__lang === "en"
            ? "Explore the products currently available and the line in development."
            : "Esplora i prodotti attualmente disponibili e la linea in sviluppo."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-8" aria-label="Filtri">
          <div>
            <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-3">Cerca</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-ink" />
              <Input
                type="search"
                placeholder={t("filters.search")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 rounded-none border-soft bg-white"
                data-testid="products-search"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-3">
              Linea
            </label>
            <ul className="space-y-1">
              <FilterOption active={!line} onClick={() => setFilter("linea", "")} label={`${t("filters.all_lines")}`} />
              <FilterOption
                active={line === "pet"}
                onClick={() => setFilter("linea", "pet")}
                label={`${t("filters.line_pet")} (${petCount || 0})`}
                accent="pet"
                testId="filter-pet"
              />
              <FilterOption
                active={line === "human"}
                onClick={() => setFilter("linea", "human")}
                label={`${t("filters.line_human")}`}
                accent="human"
                testId="filter-human"
              />
            </ul>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-3">
              {t("filters.species")}
            </label>
            <ul className="space-y-1">
              <FilterOption active={!species} onClick={() => setFilter("specie", "")} label={lang === "en" ? "All" : "Tutti"} />
              <FilterOption active={species === "cane"} onClick={() => setFilter("specie", "cane")} label={t("filters.dog")} testId="filter-dog" />
              <FilterOption active={species === "gatto"} onClick={() => setFilter("specie", "gatto")} label={t("filters.cat")} testId="filter-cat" />
              <FilterOption active={species === "persona"} onClick={() => setFilter("specie", "persona")} label={t("filters.person")} />
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {products.length === 0 ? (
            <div className="border border-dashed border-soft p-12 text-center text-muted-ink" data-testid="products-empty">
              {t("filters.no_results")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          )}

          {humanCount === 0 && line !== "pet" && (
            <div className="mt-10 border border-soft bg-white p-8" data-testid="human-coming-soon-block">
              <div className="text-xs uppercase tracking-editorial text-human mb-2">
                {lang === "en" ? "Human Line" : "Linea Human"}
              </div>
              <h3 className="font-display text-2xl text-brand">
                {lang === "en" ? "Coming soon" : "In arrivo"}
              </h3>
              <p className="mt-2 text-sm text-muted-ink max-w-lg">
                {lang === "en"
                  ? "We're working on the new BioNutrix Human line. "
                  : "Stiamo lavorando alla nuova linea BioNutrix Human. "}
                <Link to="/linea-human" className="link-underline text-brand">
                  {lang === "en" ? "Subscribe for updates" : "Iscriviti per gli aggiornamenti"}
                </Link>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterOption({ active, onClick, label, accent, testId }) {
  const dot =
    accent === "pet" ? "bg-pet" : accent === "human" ? "bg-human" : "bg-transparent";
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm border transition-colors ${
          active ? "bg-brand text-white border-brand" : "border-transparent hover:bg-cream-warm text-brand"
        }`}
      >
        {accent && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
        <span>{label}</span>
      </button>
    </li>
  );
}
